// The Dashboard's plan: which chart card draws which visual with which mark,
// and the level rows every band is cut into. Pure, so tests read it straight
// from a payload.
//
// The owner's two rules (2026-10-05, 2026-10-12):
//   - each chart kind at most twice on the Dashboard, every rectangle on a
//     baseline (bars, columns) counted as one kind;
//   - bento rows: the cards of a row are one height and fill it with their
//     visual; spans of a row sum to twelve.
// A visual whose honest marks are all spent is not drawn dishonestly: it
// becomes another view of a card of its own kind, behind that card's view
// switch, so the page keeps every chart and the cap holds.
import type { IndustryPattern, MetricValue, Overview, Stage, Visual } from "@dashboard-intelligent/api/src/modules/industry/types";
import { markKind, markOptions, type Mark, type MarkKind } from "../shared/visual";
import { isPeriodAxis } from "../shared/metric";
import { stageMetrics } from "../shared/metric";

/** At most this many cards of one kind on the Dashboard. */
export const KIND_MAX = 2;

/** Kinds held tighter than the cap: a waffle at most once per Dashboard (owner and critic, 2026-10-12). */
export const KIND_LIMIT: Partial<Record<MarkKind, number>> = { waffle: 1 };

/** How many cards of a kind the Dashboard may hold. */
export function limitOf(kind: MarkKind): number {
  return KIND_LIMIT[kind] ?? KIND_MAX;
}

export interface ChartView {
  visual: Visual;
  mark: Mark;
}

export interface ChartPlan {
  id: string;
  kind: MarkKind;
  /** The band the card stands in: the stage of its first view. */
  stageId: string | null;
  views: ChartView[];
  /** Two measures of the same categories, drawn as one scatter. */
  pair?: { x: Visual; y: Visual };
}

export interface BandPlan {
  id: string;
  /** Null for the visuals that belong to no listed stage. */
  stage: Stage | null;
  metrics: MetricValue[];
  charts: ChartPlan[];
}

export interface DashboardPlan {
  bands: BandPlan[];
  /** Every chart card, the hero first when there is one. */
  charts: ChartPlan[];
  counts: Record<MarkKind, number>;
  hero: ChartPlan | null;
  /** The chart that explains the North Star, opened beside its parts. */
  companion: ChartPlan | null;
  /** The opening bands, chosen by the industry's pattern and its North Star. */
  opening: OpeningBand[];
}

const KINDS: MarkKind[] = ["area", "line", "step", "bars", "funnel", "donut", "waffle", "heat", "deviation", "scatter"];

/** Visuals in page order: by the stage order, then the ones with no listed stage. */
function ordered(overview: Overview): Visual[] {
  const rank = new Map(overview.stages.map((s, i) => [s.id, i]));
  return overview.visuals
    .map((visual, index) => ({ visual, index, at: visual.stageId !== null && rank.has(visual.stageId) ? rank.get(visual.stageId)! : overview.stages.length }))
    .sort((a, b) => a.at - b.at || a.index - b.index)
    .map((entry) => entry.visual);
}

/** The cards of the page, each kind at most twice, overflow folded in as views. */
export function planCharts(
  overview: Overview,
  heroId: string | null = heroVisual(overview)?.id ?? null,
  companionId: string | null = companionVisual(overview, heroId)?.id ?? null,
): { charts: ChartPlan[]; counts: Record<MarkKind, number>; hero: ChartPlan | null; companion: ChartPlan | null } {
  const known = new Set(overview.stages.map((s) => s.id));
  const counts = Object.fromEntries(KINDS.map((k) => [k, 0])) as Record<MarkKind, number>;
  const charts: ChartPlan[] = [];
  let hero: ChartPlan | null = null;
  const all = ordered(overview);
  const heroV = heroId ? all.find((v) => v.id === heroId) : undefined;
  if (heroV) {
    const mark = markOptions(heroV)[0]!;
    counts[markKind(mark)] += 1;
    hero = { id: `hero-${heroV.id}`, kind: markKind(mark), stageId: null, views: [{ visual: heroV, mark }] };
  }
  let companion: ChartPlan | null = null;
  const compV = companionId ? all.find((v) => v.id === companionId) : undefined;
  if (compV) {
    const mark = markOptions(compV).find((m) => counts[markKind(m)] < limitOf(markKind(m))) ?? markOptions(compV)[0]!;
    counts[markKind(mark)] += 1;
    companion = { id: `pair-${compV.id}`, kind: markKind(mark), stageId: null, views: [{ visual: compV, mark }] };
  }
  // Two categorical measures of one stage over the same categories (distribution and out-of-stock per area) read as one scatter.
  const used = new Set<Visual>([heroV, compV].filter((v): v is Visual => v !== undefined));
  const cat = (v: Visual) => (v.kind === "ranked" || (v.kind === "column" && !isPeriodAxis(v.data)) ? v : null);
  for (const a of all) {
    if (used.has(a) || !cat(a) || counts.scatter >= KIND_MAX) continue;
    if (a.kind === "trend" || a.kind === "funnel" || a.kind === "part" || a.data.length < 4 || /P&L/i.test(a.title)) continue;
    const keys = a.data.map((d) => d.label).sort().join("|");
    const b = all.find(
      (v) => v !== a && !used.has(v) && cat(v) && v.stageId === a.stageId && v.kind !== "trend" && v.kind !== "funnel" && v.kind !== "part" && !/P&L/i.test(v.title) && v.data.map((d) => d.label).sort().join("|") === keys,
    );
    if (!b || b.kind === "trend" || b.kind === "funnel" || b.kind === "part") continue;
    used.add(a);
    used.add(b);
    counts.scatter += 1;
    const stageId = a.stageId !== null && known.has(a.stageId) ? a.stageId : null;
    charts.push({ id: `grafik-${a.id}-${b.id}`, kind: "scatter", stageId, views: [{ visual: a, mark: "deviation" }, { visual: b, mark: "deviation" }], pair: { x: a, y: b } });
  }
  for (const visual of all) {
    if (used.has(visual)) continue;
    const options = markOptions(visual);
    const stageId = visual.stageId !== null && known.has(visual.stageId) ? visual.stageId : null;
    const fresh = options.find((mark) => counts[markKind(mark)] < limitOf(markKind(mark)));
    if (fresh) {
      counts[markKind(fresh)] += 1;
      charts.push({ id: `grafik-${visual.id}`, kind: markKind(fresh), stageId, views: [{ visual, mark: fresh }] });
      continue;
    }
    // Every honest kind is spent: join a card of the first kind that can draw it, its own stage first, else the card with fewest views.
    let placed = false;
    for (const mark of options) {
      const kind = markKind(mark);
      const hosts = charts.filter((c) => c.kind === kind);
      if (hosts.length === 0) continue;
      const host = hosts.find((c) => c.stageId === stageId) ?? [...hosts].sort((a, b) => a.views.length - b.views.length)[0]!;
      host.views.push({ visual, mark });
      placed = true;
      break;
    }
    if (!placed) {
      // Unreachable while every kind can hold two cards; kept so no visual is ever lost.
      const mark = options[0]!;
      counts[markKind(mark)] += 1;
      charts.push({ id: `grafik-${visual.id}`, kind: markKind(mark), stageId, views: [{ visual, mark }] });
    }
  }
  return { charts, counts, hero, companion };
}

/** The ids the opening already shows: the headline figures, the North Star, and its parts. */
export function openingIds(overview: Overview): Set<string> {
  const ids = new Set(headlineOf(overview).map((m) => m.id));
  if (overview.northStar.metric) ids.add(overview.northStar.metric.id);
  for (const c of nsParts(overview)) ids.add(c.id);
  return ids;
}

/** The North Star's parts the page shows with it: computed, and not already a headline figure. */
export function nsParts(overview: Overview): MetricValue[] {
  const head = new Set(headlineOf(overview).map((m) => m.id));
  return overview.northStar.components.filter((c) => c.value !== null && !head.has(c.id));
}

/** Every band of the page: one per stage that has a metric or a chart, then the loose charts. Each figure appears once. */
export function planDashboard(overview: Overview): DashboardPlan {
  const { charts, counts, hero, companion } = planCharts(overview);
  const bands: BandPlan[] = [];
  const shown = openingIds(overview);
  for (const stage of overview.stages) {
    const metrics = stageMetrics(stage, overview.metrics).filter((m) => !shown.has(m.id));
    const own = charts.filter((c) => c.stageId === stage.id);
    if (metrics.length === 0 && own.length === 0) continue;
    bands.push({ id: stage.id, stage, metrics, charts: own });
  }
  const loose = charts.filter((c) => c.stageId === null);
  if (loose.length > 0) bands.push({ id: "lintas-tahap", stage: null, metrics: [], charts: loose });
  const lead = [hero, companion].filter((c): c is ChartPlan => c !== null);
  return { bands, charts: [...lead, ...charts], counts, hero, companion, opening: openingBands(overview, hero, companion) };
}

export type RowItem = { type: "tiles"; span: number } | { type: "chart"; chart: ChartPlan; span: number };

/** Chart cards with no tiles beside them, cut into rows that fill twelve: pairs, a triple when the count is odd, one alone at full width. */
function chartRows(charts: ChartPlan[]): RowItem[][] {
  const rows: RowItem[][] = [];
  let rest = charts;
  if (rest.length === 1) return [[{ type: "chart", chart: rest[0]!, span: 12 }]];
  if (rest.length % 2 === 1) {
    rows.push(rest.slice(0, 3).map((chart) => ({ type: "chart" as const, chart, span: 4 })));
    rest = rest.slice(3);
  }
  for (let i = 0; i < rest.length; i += 2) {
    rows.push([
      { type: "chart", chart: rest[i]!, span: 6 },
      { type: "chart", chart: rest[i + 1]!, span: 6 },
    ]);
  }
  return rows;
}

/** The rows of one band: its figures as one strip, then its charts in rows of their own. */
export function bandRows(band: BandPlan): RowItem[][] {
  const strip: RowItem[][] = band.metrics.length > 0 ? [[{ type: "tiles", span: 12 }]] : [];
  return [...strip, ...(band.charts.length > 0 ? chartRows(band.charts) : [])];
}

/** Spans of the headline band: four across, three, two, or one. */
export function headlineSpan(count: number): 3 | 4 | 6 | 12 {
  return count >= 4 ? 3 : count === 3 ? 4 : count === 2 ? 6 : 12;
}

/** Spans of the North Star row: the trend and its parts. */
export function northStarSpans(hasTrend: boolean, parts: number): [number, number] | [number] {
  if (!hasTrend || parts === 0) return [12];
  return parts <= 4 ? [8, 4] : [6, 6];
}

/* ---------------------------------------------------------------------
 * The opening: each pattern leads with its own question.
 *   funnel      the conversion flow leads, the North Star beside it, then the headline figures
 *   kapasitas   the North Star leads beside how the capacity is used, then the headline figures
 *   langganan   the headline figures, then the North Star beside retention
 *   distribusi  the headline figures, then the channel or region share beside the North Star
 *   dampak      reach and outcome across the full width, then the headline figures
 * The North Star card is as wide as its parts need (4, 5, or 6 of twelve).
 * ------------------------------------------------------------------- */

/** What the hero visual of each pattern is about, in the words a visual's title uses. */
const HERO_WORDS: Record<IndustryPattern, RegExp[]> = {
  funnel: [],
  langganan: [/churn|retensi|cohort|subscriber|berhenti/i, /status nasabah|member aktif|aktif/i],
  kapasitas: [/okupansi|utilisasi|occupancy/i, /throughput|backlog|kapasitas/i, /waktu tunggu|check-in/i],
  distribusi: [/sell-out per channel|per channel/i, /per area|distribution|wilayah/i],
  dampak: [/penerima manfaat|dijangkau/i, /outcome|reach/i],
};

/** The visual that opens the page for this industry's pattern, or null when it has none. */
export function heroVisual(overview: Overview): Visual | null {
  const pattern = overview.industry.pattern;
  if (pattern === "funnel") return overview.visuals.find((v) => v.kind === "funnel") ?? null;
  for (const words of HERO_WORDS[pattern]) {
    const hit = overview.visuals.find((v) => v.kind !== "funnel" && words.test(v.title));
    if (hit) return hit;
  }
  return null;
}

export type OpeningItem = { type: "north-star"; span: number } | { type: "hero"; span: number } | { type: "parts"; span: number } | { type: "companion"; span: number };

const STOP = new Set(["per", "dan", "yang", "total", "bulan", "rata", "rata-rata", "dari", "untuk", "dengan"]);

/** The chart that explains the North Star: one whose title shares a word with it, a trend first; else the first trend. */
export function companionVisual(overview: Overview, heroId: string | null): Visual | null {
  const words = overview.northStar.label
    .toLowerCase()
    .split(/[^a-z0-9&-]+/)
    .filter((w) => w.length >= 4 && !STOP.has(w));
  const pool = overview.visuals.filter((v) => v.id !== heroId && v.kind !== "funnel");
  const hits = pool.filter((v) => words.some((w) => v.title.toLowerCase().includes(w)));
  // A funnel already shows time-free flow; its North Star is explained by where the result comes from (a split), others by its course over time.
  const prefer = overview.industry.pattern === "funnel" ? (v: Visual) => v.kind !== "trend" : (v: Visual) => v.kind === "trend";
  return hits.find(prefer) ?? hits[0] ?? pool.find((v) => v.kind === "trend") ?? null;
}
export type OpeningBand = { type: "kpi" } | { type: "row"; items: OpeningItem[] };

/** How wide the North Star card is, from how many parts its formula has. */
export function northStarSpan(parts: number): 4 | 5 | 6 {
  return parts <= 3 ? 4 : parts <= 6 ? 5 : 6;
}

export function openingBands(overview: Overview, hero: ChartPlan | null, companion: ChartPlan | null = null): OpeningBand[] {
  const parts = nsParts(overview).length;
  const trend = (overview.northStar.metric?.trend.length ?? 0) >= 2;
  const ns = northStarSpan(parts);
  const kpi: OpeningBand[] = headlineOf(overview).length > 0 ? [{ type: "kpi" }] : [];
  // The parts of a North Star that has its own trend stand as one strip of their own height.
  const partsRow: OpeningBand[] = trend && parts > 0 ? [{ type: "row", items: [{ type: "parts", span: 12 }] }] : [];
  // With no trend the North Star is its figure and its parts: they sit beside the chart that explains it, which grows to them.
  if (!trend) {
    const heroRow: OpeningBand[] = hero ? [{ type: "row", items: [{ type: "hero", span: 12 }] }] : [];
    const nsRow: OpeningBand[] = companion
      ? [{ type: "row", items: [{ type: "north-star", span: ns }, { type: "companion", span: 12 - ns }] }]
      : [{ type: "row", items: [{ type: "north-star", span: 12 }] }];
    switch (overview.industry.pattern) {
      case "funnel":
      case "dampak":
        return [...heroRow, ...nsRow, ...kpi];
      case "kapasitas":
        return [...nsRow, ...heroRow, ...kpi];
      default:
        return [...kpi, ...heroRow, ...nsRow];
    }
  }
  const explain: OpeningBand[] = companion ? [{ type: "row", items: [{ type: "companion", span: 12 }] }] : [];
  const pair = (heroFirst: boolean): OpeningBand[] => {
    if (!hero) return [{ type: "row", items: [{ type: "north-star", span: 12 }] }];
    const items: OpeningItem[] = [
      { type: "hero", span: 12 - ns },
      { type: "north-star", span: ns },
    ];
    return [{ type: "row", items: heroFirst ? items : items.reverse() }];
  };
  switch (overview.industry.pattern) {
    case "funnel":
      return [...pair(true), ...partsRow, ...explain, ...kpi];
    case "kapasitas":
      return [...pair(false), ...partsRow, ...kpi, ...explain];
    case "langganan":
      return [...kpi, ...pair(false), ...partsRow, ...explain];
    case "distribusi":
      return [...kpi, ...pair(true), ...explain, ...partsRow];
    case "dampak":
      return hero ? [{ type: "row", items: [{ type: "hero", span: 12 }] }, ...partsRow, ...kpi, ...explain] : [...kpi, ...pair(true), ...partsRow, ...explain];
  }
}

/** The headline figures, without the North Star itself (it has its own card). */
export function headlineOf(overview: Overview): MetricValue[] {
  const ns = overview.northStar.metric?.id;
  return overview.headline.filter((m) => m.id !== ns).slice(0, 4);
}

/** The opening as a signature: per band, its cards' kinds and spans. Two industries never share their first two. */
export function openingSignature(overview: Overview): string[] {
  const plan = planDashboard(overview);
  const trend = (overview.northStar.metric?.trend.length ?? 0) >= 2;
  const kpis = headlineOf(overview);
  const kpiSpan = headlineSpan(kpis.length);
  return plan.opening.map((band) =>
    band.type === "kpi"
      ? kpis.map((m) => `${m.target && m.value !== null ? "kpi-target" : "kpi"}:${kpiSpan}`).join(" ")
      : band.items
          .map((item) =>
            item.type === "hero"
              ? `hero-${plan.hero!.views[0]!.mark}:${item.span}`
              : item.type === "companion"
                ? `pair-${plan.companion!.views[0]!.mark}:${item.span}`
                : item.type === "parts"
                  ? `parts-${nsParts(overview).length}tiles:${item.span}`
                  : `ns-${trend ? "trend" : "tiles"}:${item.span}`,
          )
          .join(" "),
  );
}
